import { useMemo, useState } from "react";
import {
  Box,
  Check,
  Download,
  Gauge,
  HardDrive,
  Search,
  ShieldAlert,
} from "lucide-react";
import { Dialog } from "./Dialog";
import { MODEL_FAMILIES, MODELS } from "../models";
import type { GpuCapability, ModelDefinition, ModelLoadState } from "../types";

interface ModelLibraryProps {
  open: boolean;
  selectedModel: ModelDefinition;
  loadState: ModelLoadState;
  gpu: GpuCapability | null;
  onClose: () => void;
  onSelect: (model: ModelDefinition) => void;
  onLoad: (model: ModelDefinition) => void;
}

function supportCopy(model: ModelDefinition): { label: string; className: string } {
  if (model.browserSupport === "ready") return { label: "利用可能", className: "support-ready" };
  if (model.browserSupport === "high-memory") {
    return { label: "高性能", className: "support-warning" };
  }
  return { label: "準備中", className: "support-catalog" };
}

export function ModelLibrary({
  open,
  selectedModel,
  loadState,
  gpu,
  onClose,
  onSelect,
  onLoad,
}: ModelLibraryProps) {
  const [query, setQuery] = useState("");
  const [family, setFamily] = useState<string>("すべて");
  const filtered = useMemo(() => {
    const normalized = query.trim().toLowerCase();
    return MODELS.filter((model) => {
      const familyMatches = family === "すべて" || model.family === family;
      const queryMatches =
        !normalized ||
        `${model.name} ${model.sourceRepo} ${model.parameterLabel}`.toLowerCase().includes(normalized);
      return familyMatches && queryMatches;
    });
  }, [family, query]);

  return (
    <Dialog
      open={open}
      wide
      title="AIを選ぶ"
      description="用途に合わせて使うAIを選べます。"
      onClose={onClose}
    >
      <div className="model-toolbar">
        <label className="search-field">
          <Search size={17} />
          <span className="sr-only">モデルを検索</span>
          <input
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder="AI名を検索"
          />
        </label>
        <div className="family-tabs" role="tablist" aria-label="モデルファミリー">
          {["すべて", ...MODEL_FAMILIES].map((item) => (
            <button
              key={item}
              type="button"
              className={family === item ? "active" : ""}
              onClick={() => setFamily(item)}
              role="tab"
              aria-selected={family === item}
            >
              {item}
            </button>
          ))}
        </div>
      </div>

      <div className="model-environment">
        <Gauge size={17} />
        <span>
          {gpu?.available
            ? "AIを利用できます"
            : "AIを確認しています。"}
        </span>
      </div>

      <div className="model-grid">
        {filtered.map((model) => {
          const support = supportCopy(model);
          const selected = selectedModel.id === model.id;
          const loading =
            selected && (loadState.status === "loading" || loadState.status === "checking");
          const ready =
            selected && loadState.status === "ready" && loadState.modelId === model.id;
          const selectable = model.engine === "server";
          return (
            <article key={model.id} className={`model-card${selected ? " selected" : ""}`}>
              <div className="model-card-top">
                <span className={`support-badge ${support.className}`}>{support.label}</span>
                {selected && <span className="selected-mark"><Check size={14} /> 選択中</span>}
              </div>
              <div className="model-symbol"><Box size={22} /></div>
              <h3>{model.name}</h3>
              <p className="model-params">{model.parameterLabel}</p>
              <p className="runtime-repo">用途に合わせて切り替えできます</p>
              <p className="model-note">{model.note}</p>
              <div className="model-specs">
                {model.estimatedDownload && (
                  <span><HardDrive size={14} /> {model.estimatedDownload}</span>
                )}
                {model.minimumGpuMemory && <span>GPU {model.minimumGpuMemory}</span>}
              </div>
              {selectable ? (
                <div className="model-actions">
                  <button type="button" className="button button--secondary" onClick={() => onSelect(model)}>
                    選択
                  </button>
                  <button
                    type="button"
                    className="button button--primary"
                    onClick={() => {
                      onSelect(model);
                      onLoad(model);
                    }}
                    disabled={!gpu?.available || loading || ready}
                  >
                    {ready ? <><Check size={16} /> 利用可能</> : loading ? "準備中…" : <><Download size={16} /> 読み込む</>}
                  </button>
                </div>
              ) : (
                <div className="unsupported-note">
                  <ShieldAlert size={15} /> まだ利用できません
                </div>
              )}
            </article>
          );
        })}
      </div>
      {!filtered.length && <div className="empty-filter">条件に一致するモデルはありません。</div>}
    </Dialog>
  );
}
